import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import { QUESTIONS, CATEGORIES, mergeDynamicOptions, mergeMaterialsList } from "@/lib/questions";
import api from "@/lib/api";

export default function Wizard() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [dynamicOpts, setDynamicOpts] = useState(null);
  const [materialsList, setMaterialsList] = useState(null);
  const mergedQuestions = useMemo(
    () => mergeMaterialsList(mergeDynamicOptions(QUESTIONS, dynamicOpts), materialsList),
    [dynamicOpts, materialsList]
  );
  const questions = mergedQuestions[category];
  const cat = CATEGORIES[category];

  useEffect(() => {
    api.get("/wizard-options")
      .then((r) => setDynamicOpts(r.data))
      .catch(() => setDynamicOpts(null));
    api.get("/materials-list")
      .then((r) => setMaterialsList(r.data.materials || null))
      .catch(() => setMaterialsList(null));
  }, []);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  if (!questions || !cat) {
    return (
      <div className="min-h-screen tm-grid-bg">
        <Header />
        <div className="max-w-2xl mx-auto p-8 text-center">
          <p className="text-slate-300 mb-6">Categoria non valida.</p>
          <Link to="/" className="tm-btn-primary" data-testid="back-home-btn">Torna alla home</Link>
        </div>
      </div>
    );
  }

  const q = questions[step];
  const total = questions.length;

  const setValue = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));
  const setRangeValue = (fieldKey, value) => setAnswers((a) => ({ ...a, [fieldKey]: value }));

  const goToResults = () => {
    sessionStorage.setItem("tm_answers", JSON.stringify(answers));
    sessionStorage.setItem("tm_category", category);
    navigate("/results");
  };

  // Skip questions whose skip_if predicate returns true given current answers
  const shouldSkip = (idx) => {
    const question = questions[idx];
    return question && typeof question.skip_if === "function" && question.skip_if(answers);
  };

  const nextIndex = (from) => {
    let i = from + 1;
    while (i < total && shouldSkip(i)) i++;
    return i;
  };

  const prevIndex = (from) => {
    let i = from - 1;
    while (i >= 0 && shouldSkip(i)) i--;
    return i;
  };

  const next = () => {
    const i = nextIndex(step);
    if (i < total) setStep(i);
    else goToResults();
  };

  const skip = () => {
    // Clear current answer(s) for this question then move forward
    if (q.type === "range") {
      const cleared = { ...answers };
      (q.fields || []).forEach((f) => delete cleared[f.key]);
      setAnswers(cleared);
    } else {
      const cleared = { ...answers };
      delete cleared[q.id];
      setAnswers(cleared);
    }
    const i = nextIndex(step);
    if (i < total) setStep(i);
    else goToResults();
  };

  const back = () => {
    const i = prevIndex(step);
    if (i >= 0) setStep(i);
    else navigate("/");
  };

  const currentValue = answers[q.id];

  return (
    <div className="min-h-screen tm-grid-bg">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-40 sm:pb-10">
        {/* Progress */}
        <div className="mb-6 sm:mb-10">
          <div className="flex justify-between mb-3 font-mono text-xs uppercase tracking-widest text-slate-400">
            <span data-testid="wizard-category-label">
              {cat.code} · {cat.title}
            </span>
            <span data-testid="wizard-step-label">
              Step {step + 1} / {total}
            </span>
          </div>
          <div className="flex gap-1">
            {questions.map((_, i) => (
              <div
                key={i}
                className={`flex-1 tm-seg ${i <= step ? "tm-seg-active" : ""}`}
                data-testid={`progress-seg-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -40, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="tm-card p-5 sm:p-8"
            data-testid={`question-${q.id}`}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-white tracking-tighter mb-2" data-testid="question-label">
              {q.label}
            </h2>
            {q.hint && (
              <p className="text-slate-400 text-sm mb-6" data-testid="question-hint">{q.hint}</p>
            )}
            {!q.hint && <div className="mb-6" />}

            {q.type === "radio" && (
              <div className="grid gap-3">
                {q.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setValue(q.id, opt.value)}
                    className={`tm-radio-card text-left flex justify-between items-center ${currentValue === opt.value ? "active" : ""}`}
                    data-testid={`option-${q.id}-${opt.value}`}
                  >
                    <span className="text-white">{opt.label}</span>
                    <span className={`w-4 h-4 border ${currentValue === opt.value ? "border-orange-500 bg-orange-500" : "border-slate-500"}`} />
                  </button>
                ))}
              </div>
            )}

            {q.type === "checkboxes" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {q.options.map((opt) => {
                  const active = (currentValue || []).includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => {
                        const arr = currentValue || [];
                        setValue(q.id, active ? arr.filter((v) => v !== opt.value) : [...arr, opt.value]);
                      }}
                      className={`tm-radio-card text-left flex justify-between items-center ${active ? "active" : ""}`}
                      data-testid={`option-${q.id}-${opt.value}`}
                    >
                      <span className="text-white text-sm">{opt.label}</span>
                      <span className={`w-4 h-4 border ${active ? "border-orange-500 bg-orange-500" : "border-slate-500"}`} />
                    </button>
                  );
                })}
              </div>
            )}

            {q.type === "boolean" && (
              <div className="grid grid-cols-2 gap-3">
                {[{ v: true, l: "Sì" }, { v: false, l: "No" }].map((opt) => (
                  <button
                    key={String(opt.v)}
                    onClick={() => setValue(q.id, opt.v)}
                    className={`tm-radio-card text-center ${currentValue === opt.v ? "active" : ""}`}
                    data-testid={`option-${q.id}-${opt.v}`}
                  >
                    <span className="font-heading text-lg">{opt.l}</span>
                  </button>
                ))}
              </div>
            )}

            {q.type === "text" && (
              <input
                type="text"
                className="tm-input"
                placeholder={q.placeholder}
                value={currentValue || ""}
                onChange={(e) => setValue(q.id, e.target.value)}
                data-testid={`input-${q.id}`}
              />
            )}

            {q.type === "textarea" && (
              <textarea
                className="tm-textarea"
                placeholder={q.placeholder}
                value={currentValue || ""}
                onChange={(e) => setValue(q.id, e.target.value)}
                data-testid={`input-${q.id}`}
              />
            )}

            {q.type === "number" && (
              <input
                type="number"
                inputMode="decimal"
                className="tm-input"
                placeholder={q.placeholder}
                value={currentValue ?? ""}
                onChange={(e) => setValue(q.id, e.target.value === "" ? "" : Number(e.target.value))}
                data-testid={`input-${q.id}`}
              />
            )}

            {q.type === "select" && (
              <select
                className="tm-input"
                value={currentValue || ""}
                onChange={(e) => setValue(q.id, e.target.value)}
                data-testid={`select-${q.id}`}
              >
                <option value="">-- Seleziona --</option>
                {q.options.map((o) => (
                  <option key={o} value={o}>{o || "Nessuno"}</option>
                ))}
              </select>
            )}

            {q.type === "range" && (
              <div className="grid grid-cols-2 gap-4">
                {q.fields.map((f) => (
                  <div key={f.key}>
                    <label className="tm-label">{f.label}</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      className="tm-input"
                      placeholder={f.placeholder}
                      value={answers[f.key] ?? ""}
                      onChange={(e) => setRangeValue(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid={`input-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav — su mobile fissa in basso, a portata di pollice */}
        <div
          className="fixed sm:static inset-x-0 bottom-0 z-30 sm:z-auto bg-slate-950/95 sm:bg-transparent backdrop-blur-md sm:backdrop-blur-none border-t border-slate-800 sm:border-0 px-4 pt-3 sm:p-0 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          data-testid="wizard-nav"
        >
          <button onClick={back} className="tm-btn-outline text-sm sm:text-base !px-2 sm:!px-6 order-2 sm:order-1" data-testid="wizard-back-btn">
            ← {step === 0 ? "Home" : <><span className="sm:hidden">Indietro</span><span className="hidden sm:inline">Torna al precedente</span></>}
          </button>
          <button
            onClick={skip}
            className="tm-btn-outline text-slate-300 text-sm sm:text-base !px-2 sm:!px-6 order-3 sm:order-2"
            data-testid="wizard-skip-btn"
            title="Salta questa domanda senza rispondere"
          >
            Non lo so — salta
          </button>
          <button
            onClick={next}
            className="tm-btn-primary col-span-2 sm:col-span-1 order-1 sm:order-3"
            data-testid="wizard-next-btn"
          >
            {step === total - 1 ? "Mostra prodotti →" : "Avanti →"}
          </button>
        </div>
      </div>
    </div>
  );
}
